"use client";

import React from "react";
import { Instagram, Linkedin, Mail, MapPin, Twitter, Send, Loader, Facebook } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 animate-pulse rounded-md bg-primary-foreground/10" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="flex flex-col gap-3">
        <li data-index={0}><Link to="/HomePage" className="text-primary-foreground/80 transition-colors hover:text-primary-foreground">Home</Link></li>
        <li data-index={1}><Link to="/ServicesPage" className="text-primary-foreground/80 transition-colors hover:text-primary-foreground">Services</Link></li>
        <li data-index={2}><Link to="/ResultsPage" className="text-primary-foreground/80 transition-colors hover:text-primary-foreground">Results</Link></li>
        <li data-index={3}><Link to="/ContactPage" className="text-primary-foreground/80 transition-colors hover:text-primary-foreground">Contact</Link></li>
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-primary-foreground/80 transition-colors hover:text-primary-foreground">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterFormConsumer() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg border border-primary-foreground/20 bg-primary-foreground/10 p-4">
        <p className="text-sm text-primary-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="flex gap-2">
              <FormControl>
                <Input type="email" placeholder="Email address" className="border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-primary-foreground/30" {...field} />
              </FormControl>
              <Button type="submit" disabled={isSubmitting} className="bg-accent text-accent-foreground hover:bg-accent/90 shrink-0" size="icon" aria-label="Subscribe to newsletter">
                {isSubmitting ? <Loader className="size-4 animate-spin" /> : <Send className="size-4" />}
              </Button>
            </div>
            <FormMessage className="text-primary-foreground/80" />
          </FormItem>} />
      {submitError && <p className="text-sm text-destructive-foreground">{submitError}</p>}
    </Form>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const socialLinks = [{
    icon: Linkedin,
    href: "https://www.linkedin.com/in/aliceslho/",
    label: "LinkedIn"
  }, {
    icon: Instagram,
    href: "https://instagram.com/alice_emplifyworld",
    label: "Instagram"
  }, {
    icon: Facebook,
    href: "#",
    label: "Facebook"
  }];
  return <section id="footer" className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-16 md:px-6 lg:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          
          {}
          <div className="flex flex-col gap-6">
            <Link to="/HomePage" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1589716/img/instagram_brand_logo.png" alt="Instagram Logo" className="h-7 w-auto" />
            </Link>
            <p className="font-sans text-sm leading-relaxed text-primary-foreground/80">
              Bridging creative vision with strategic success through personalized consulting and high-impact programs.
            </p>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="font-serif text-lg font-semibold tracking-tight">Quick Links</h3>
            <nav className="font-sans text-sm">
              <MenuProvider menu_id="44">
                <FooterMenuConsumer />
              </MenuProvider>
            </nav>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="font-serif text-lg font-semibold tracking-tight">Contact Us</h3>
            <ul className="flex flex-col gap-4 font-sans text-sm text-primary-foreground/80">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 size-4 shrink-0" />
                <span>Karel Doormanlaan 85, 1271CB, Huizen, Netherlands</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="size-4 shrink-0" />
                <Link to="mailto:hello@creativeconnectors.com" className="transition-colors hover:text-primary-foreground">hello@emplify.world</Link>
              </li>
            </ul>
            <div className="mt-2 flex gap-4">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} data-index={i} to={social.href} newTab className="rounded-full border border-primary-foreground/20 p-2 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground inline-flex">
                    <Icon className="size-4" strokeWidth={1.5} />
                    <span className="sr-only">{social.label}</span>
                  </Link>;
            })}
            </div>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="font-serif text-lg font-semibold tracking-tight">Stay Connected</h3>
            <p className="font-sans text-sm text-primary-foreground/80">Subscribe to our newsletter for latest insights and more behind the scenes stories and tips.</p>
            <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Thank you for subscribing!">
              <NewsletterFormConsumer />
            </FormProvider>
          </div>
        </div>

        {}
        <div className="mt-16 flex flex-col items-center gap-6 pt-8 md:mt-20 md:flex-row md:justify-between md:gap-4 border-t border-primary-foreground/20">
          <p className="font-sans text-[12px] text-primary-foreground/60 text-center md:text-left">
            © {currentYear} Emplify.World. All rights reserved.
          </p>
          <div className="flex gap-6 font-sans text-[12px] text-primary-foreground/60">
            <Link to="#" className="transition-colors hover:text-primary-foreground">
              Privacy Policy
            </Link>
            <Link to="#" className="transition-colors hover:text-primary-foreground">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </section>;
}